import cn from "classnames";
import { dateTimeToView } from "helpers/date";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import { alphabeticalSort } from "helpers/system";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { namesList as counterpartiesList } from "sources/books/cargoOwners";
import { list as employeesList } from "sources/books/employees";
import CommonReport from "./commonReport";

const RESPONSIBLE_FALLBACK = "Без ответственного";
const ROW_COUNT_REGEXP = / \(\d+\)$/;
const HIRED_CAR_COUNTERPARTY_NAME_PROPERTY_NAME = "HiredCarCounterpartyName";
const RESPONSIBLE_ID_PROPERTY_NAME = "ResponsibleId";
const RESPONSIBLE_NAME_PROPERTY_NAME = "ResponsibleName";

const getResponsibleColumnInfo = (columns = []) => {
    const idColumn = columns.find((column) => column.propertyName === RESPONSIBLE_ID_PROPERTY_NAME);
    const nameColumn = columns.find((column) => column.propertyName === RESPONSIBLE_NAME_PROPERTY_NAME);

    return {
        idAccessor: idColumn ? columnName(idColumn) : null,
        nameAccessor: nameColumn ? columnName(nameColumn) : null,
    };
};

class HiredCarReport extends CommonReport {
    responsibleColumnInfo = () => getResponsibleColumnInfo(this.props.data?.columns);

    hasRowsGroupedByBackend = () => {
        const { data = [] } = this.props.data || {};
        return data.some((row) => Array.isArray(row.distributionBids) && row.distributionBids.length > 0);
    };

    responsibleName = (row) => {
        const { nameAccessor } = this.responsibleColumnInfo();

        return (nameAccessor ? row?.[nameAccessor] : null) || RESPONSIBLE_FALLBACK;
    };

    responsibleNameFromRows = (...rows) => {
        const { nameAccessor } = this.responsibleColumnInfo();

        return rows.map((row) => (nameAccessor ? row?.[nameAccessor] : null)).find(Boolean) || RESPONSIBLE_FALLBACK;
    };

    rowCountText = (name, count) => {
        return count && !ROW_COUNT_REGEXP.test(name) ? ` (${count})` : "";
    };

    cellProps = (column) => {
        const { idAccessor, nameAccessor } = this.responsibleColumnInfo();
        const accessor = columnName(column);
        const isResponsibleColumn = [idAccessor, nameAccessor].includes(accessor);

        return {
            Header: isResponsibleColumn ? "Ответственный" : column.label,
            accessor,
            Cell: (props) => {
                const { counterpartyId } = props.row.values;
                const uniqueId = { [`data-${props.column.id}`.toLowerCase()]: counterpartyId };
                switch (props.column.id) {
                    case "id": {
                        if (!props.value) {
                            return "";
                        }

                        return (
                            <div className="long-text" {...uniqueId}>
                                <Link to={paths.distributionBidShow(props.value)} target="_blank">
                                    {props.value}
                                </Link>
                            </div>
                        );
                    }
                    case "counterpartyName": {
                        if (!props.row.original.distributionBids?.length) {
                            return (
                                <div className="long-text" {...uniqueId}>
                                    {props.value}
                                </div>
                            );
                        }
                        const onClick = () => {
                            props.modal({
                                name: "infoModal",
                                windowMod: "wide",
                                title: props.value,
                                body: props.renderRowSubComponent(props.row),
                            });
                        };
                        return (
                            <div className="cursor-pointer" onClick={onClick} {...uniqueId}>
                                <i
                                    className={cn("r-item__expander", {
                                        "icon-uEAAE-angle-right-solid": !props.row.isExpanded,
                                        "icon-uEA61-angle-down": props.row.isExpanded,
                                    })}
                                />
                                <span {...uniqueId}>
                                    {props.column.id === "counterpartyName"
                                        ? hiddenUserData(props.value, { type: "name" })
                                        : props.value}
                                </span>
                            </div>
                        );
                    }
                    case nameAccessor: {
                        const responsibleName = props.value || RESPONSIBLE_FALLBACK;
                        const bidsCount = props.row.original.distributionBids?.length;

                        if (!bidsCount) {
                            return (
                                <div className="long-text" {...uniqueId}>
                                    {hiddenUserData(responsibleName, { type: "name" })}
                                </div>
                            );
                        }

                        const onClick = () => {
                            props.modal({
                                name: "infoModal",
                                windowMod: "wide",
                                title: responsibleName,
                                body: props.renderRowSubComponent(props.row),
                            });
                        };

                        return (
                            <div className="cursor-pointer" onClick={onClick} {...uniqueId}>
                                <i
                                    className={cn("r-item__expander", {
                                        "icon-uEAAE-angle-right-solid": !props.row.isExpanded,
                                        "icon-uEA61-angle-down": props.row.isExpanded,
                                    })}
                                />
                                <span {...uniqueId}>
                                    {hiddenUserData(responsibleName, { type: "name" })}
                                    {this.rowCountText(responsibleName, bidsCount)}
                                </span>
                            </div>
                        );
                    }
                    case idAccessor: {
                        const { subRows } = props.row;
                        const firstRow = subRows?.[0]?.original || props.row.original;
                        const responsibleName = this.responsibleName(firstRow);

                        return (
                            <span data-hired-car-responsible-id={props.value}>
                                {hiddenUserData(responsibleName, { type: "name" })}
                                {this.rowCountText(responsibleName, subRows?.length)}
                            </span>
                        );
                    }
                    default: {
                        return (
                            <div className="long-text" {...uniqueId}>
                                {formatValue(props.value, column.clrType)}
                            </div>
                        );
                    }
                }
            },
            Footer: () => this.footerVal(column),
            ...typeSpecificProps(column),
            ...(column.propertyName === "CounterpartyName"
                ? {
                      filter: (rows, _id, filterValue) => {
                          if (!filterValue || !filterValue?.value) {
                              return rows;
                          }
                          const value = filterValue.value;
                          return rows.filter((row) => row.values.counterpartyId === value);
                      },
                      Filter: reportFilterWrapper((props) => (
                          <SelectColumnFilter
                              {...props}
                              selectProps={{
                                  url: counterpartiesList,
                                  inputProps: { name: "counterpartyId" },
                                  propName: "name",
                              }}
                          />
                      )),
                      sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "counterpartyName"),
                  }
                : {}),
            ...(column.propertyName === HIRED_CAR_COUNTERPARTY_NAME_PROPERTY_NAME
                ? {
                      filter: (rows, _id, filterValue) => {
                          if (!filterValue || !filterValue?.value) {
                              return rows;
                          }
                          const value = filterValue.value;
                          return rows.filter((row) => row.values.hiredCarCounterpartyId === value);
                      },
                      Filter: reportFilterWrapper((props) => (
                          <SelectColumnFilter
                              {...props}
                              selectProps={{
                                  url: counterpartiesList,
                                  inputProps: { name: "hiredCarCounterpartyId" },
                                  propName: "name",
                              }}
                          />
                      )),
                      sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "hiredCarCounterpartyName"),
                  }
                : {}),
            ...(column.propertyName === RESPONSIBLE_NAME_PROPERTY_NAME
                ? {
                      filter: (rows, _id, filterValue) => {
                          if (!filterValue || !filterValue?.value) {
                              return rows;
                          }
                          const value = filterValue.value;
                          return rows.filter((row) => row.original.responsibleId === value);
                      },
                      Filter: reportFilterWrapper((props) => (
                          <SelectColumnFilter
                              {...props}
                              selectProps={{
                                  url: employeesList,
                                  inputProps: { name: "responsibleId" },
                                  propName: "fullNameOrEmail",
                              }}
                          />
                      )),
                      sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "responsibleName"),
                  }
                : {}),
        };
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};
        return columns
            .map((column) => {
                return this.cellProps(column);
            })
            .concat([{ accessor: "counterpartyId" }, { accessor: "hiredCarCounterpartyId" }]);
    };

    renderRowSubComponent = (row, isPrintForm) => {
        if (!row.original?.distributionBids || row.original.distributionBids.length === 0) {
            return null;
        }
        const tableProps = isPrintForm
            ? {
                  className: "p-3 reports--bg-bluelight",
                  style: {
                      minWidth: "100%",
                      width: row.cells.reduce((acc, cell) => acc + cell.column.width, 0),
                  },
              }
            : {
                  className: "table-responsive",
              };
        return (
            <div {...tableProps}>
                <table className="table table-sm mb-0 border-bottom border-secondary bg-secondary">
                    <thead>
                        <tr className="table-active">
                            <th>#</th>
                            <th>Номер заказа</th>
                            <th>Дата заказа</th>
                            <th>Ответственный</th>
                            <th>Маршрут</th>
                            <th>Стоимость</th>
                            <th>НДС</th>
                            <th>Стоимость для перевозчика</th>
                            <th>НДС перевозчика</th>
                            <th>Прибыль</th>
                            <th>Маржинальность, %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {row.original.distributionBids.map((item, index) => (
                            <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td data-id={item.id}>
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={paths.distributionBidShow(item.id)}
                                    >
                                        {item.id}
                                    </a>
                                </td>
                                <td data-completed={item.id}>
                                    {item.hiredCarCompletedAtOffset
                                        ? hiddenUserData(
                                              `${dateTimeToView(item.hiredCarCompletedAtOffset, "DD.MM.YYYY HH:mm Z")}`,
                                          )
                                        : null}
                                </td>
                                <td data-responsible={item.id}>
                                    {hiddenUserData(this.responsibleNameFromRows(item, row.original), {
                                        type: "name",
                                    })}
                                </td>
                                <td data-route={item.id}>{item.route}</td>
                                <td data-price={item.id}>
                                    {hiddenUserData(formatValue(item.price, "double"), { type: "price" })}
                                </td>
                                <td data-vat-price={item.id}>
                                    {hiddenUserData(formatValue(item.vatPrice, "double"), { type: "price" })}
                                </td>
                                <td data-hired-car-price={item.id}>
                                    {hiddenUserData(formatValue(item.hiredCarPrice, "double"), {
                                        type: "price",
                                    })}
                                </td>
                                <td data-hired-car-vat={item.id}>
                                    {hiddenUserData(formatValue(item.hiredCarVatPrice, "double"))}
                                </td>
                                <td data-profit={item.id}>
                                    {hiddenUserData(formatValue(item.profit, "double"), { type: "price" })}
                                </td>
                                <td data-marginality={item.id}>
                                    {hiddenUserData(formatValue(item.marginality, "double"), {
                                        type: "percent",
                                    })}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    tableExtraProps = () => {
        const { idAccessor, nameAccessor } = this.responsibleColumnInfo();
        const groupByResponsible =
            this.props.groupByResponsible && idAccessor && nameAccessor && !this.hasRowsGroupedByBackend();
        const hiddenColumns = ["counterpartyId", "hiredCarCounterpartyId", "distributionBids"];

        if (idAccessor && !groupByResponsible) {
            hiddenColumns.push(idAccessor);
        }

        if (nameAccessor && groupByResponsible) {
            hiddenColumns.push(nameAccessor);
        }

        return {
            initialState: {
                hiddenColumns,
                ...(groupByResponsible ? { groupBy: [idAccessor] } : {}),
            },
            renderRowSubComponent: this.renderRowSubComponent,
        };
    };
}

const HiredCarReportWrapper = (props) => {
    const initialGroupByHiredCarCounterparty = Boolean(props.extraParams?.groupByHiredCarCounterparty);
    const [groupByHiredCarCounterparty, setGroupByHiredCarCounterparty] = useState(initialGroupByHiredCarCounterparty);
    const [groupByResponsible, setGroupByResponsible] = useState(
        Boolean(props.extraParams?.groupByResponsible) && !initialGroupByHiredCarCounterparty,
    );

    const counterpartyCheckboxCname = cn("checkbox d-inline-block mr-2", {
        "checkbox--checked": groupByHiredCarCounterparty,
    });
    const responsibleCheckboxCname = cn("checkbox d-inline-block mr-2", {
        "checkbox--checked": groupByResponsible,
    });

    const toggleCounterpartyCheckbox = () => {
        const nextGroupByHiredCarCounterparty = !groupByHiredCarCounterparty;
        const nextGroupByResponsible = nextGroupByHiredCarCounterparty ? false : groupByResponsible;
        props.getItem({
            groupByHiredCarCounterparty: nextGroupByHiredCarCounterparty,
            groupByResponsible: nextGroupByResponsible,
        });
        setGroupByHiredCarCounterparty(nextGroupByHiredCarCounterparty);
        setGroupByResponsible(nextGroupByResponsible);
    };

    const toggleResponsibleCheckbox = () => {
        const nextGroupByResponsible = !groupByResponsible;
        const nextGroupByHiredCarCounterparty = nextGroupByResponsible ? false : groupByHiredCarCounterparty;
        props.getItem({
            groupByHiredCarCounterparty: nextGroupByHiredCarCounterparty,
            groupByResponsible: nextGroupByResponsible,
        });
        setGroupByHiredCarCounterparty(nextGroupByHiredCarCounterparty);
        setGroupByResponsible(nextGroupByResponsible);
    };

    return (
        <>
            <div>
                <div className="mb-2 mr-2 btn" onClick={toggleCounterpartyCheckbox}>
                    <span className={counterpartyCheckboxCname} />
                    <small>Группировать по перевозчику</small>
                </div>
                <div className="mb-2 btn" onClick={toggleResponsibleCheckbox}>
                    <span className={responsibleCheckboxCname} />
                    <small>Группировать по ответственному</small>
                </div>
            </div>
            <HiredCarReport
                {...props}
                groupByHiredCarCounterparty={groupByHiredCarCounterparty}
                groupByResponsible={groupByResponsible}
            />
        </>
    );
};

export default HiredCarReportWrapper;
